import { useContext, useState } from 'react';
import { db } from '../../firebase/config';
import { SavedIcon } from '../../StateManagment/SavedIcon';
import Modal from '../common/Modal.jsx';
import {
    defaultReminderText,
    hasCustomMessage,
    reminderGreeting,
    reminderSignoff,
} from '../../helpers/tasks/reminderMailText';

/**
 * @typedef {object} ReminderDoc
 * @property {string} docid - Firestore-document-id
 * @property {string} [CustomMessage] - Eigen tekst; leeg of ontbrekend = standaardtekst
 */

/**
 * Venster om de tekst van één e-mailherinnering aan te passen. Bovenin de mail
 * zoals hij gaat (aanhef, middenstuk, groet); het middenstuk volgt live wat er
 * in het tekstvak staat. Leeg tekstvak = de standaardtekst, en dan gaat de
 * herinnering gewoon automatisch zoals altijd.
 *
 * Mount dit component pas als het venster open moet: het concept wordt bij het
 * mounten uit `reminder.CustomMessage` gelezen en daarna nooit meer door een
 * nieuwe snapshot overschreven.
 * AI-CONTEXT: See docs/pitfalls.md#state-draft-wiped-by-firestore-snapshot
 *
 * Eén schrijfactie, bij 'Opslaan' — geen autosave per toetsaanslag.
 * AI-CONTEXT: See docs/pitfalls.md#state-per-keystroke-write-loses-the-tail
 * AI-CONTEXT: See docs/features/bezoekherinnering-eigen-tekst.md
 *
 * Organisatienaam en antwoordadres komen als props van de ouder, die
 * `useSettings` al heeft: een eigen aanroep hier zou per opening drie
 * listeners bijzetten en het voorbeeld eerst met terugvalwaarden tonen.
 *
 * @param {object} props
 * @param {ReminderDoc} props.reminder - De herinnering waarvan de tekst wordt aangepast
 * @param {{ Title?: string }} props.task - De afspraak; de titel staat in de standaardtekst
 * @param {string} props.recipientName - Naam voor de aanhef ('' → "Beste,")
 * @param {string} props.companyName - Naam van de organisatie ('' als onbekend)
 * @param {string} props.replyTo - Antwoordadres zoals de Cloud Function het bepaalt (`resolveReplyTo`)
 * @param {boolean} props.modalOpen - Of het venster open is
 * @param {(open: boolean) => void} props.setModalOpen - Sluit (false) of opent (true) het venster
 */
const ReminderTextModal = ({ reminder, task, recipientName, companyName, replyTo, modalOpen, setModalOpen }) => {
    const [, setSaved] = useContext(SavedIcon);

    const [draft, setDraft] = useState(
        typeof reminder?.CustomMessage === 'string' ? reminder.CustomMessage : '',
    );
    const [saveError, setSaveError] = useState('');
    const [saving, setSaving] = useState(false);

    const greeting = reminderGreeting(recipientName || '');
    const [firstParagraph, secondParagraph] = defaultReminderText(task?.Title || '', companyName || '', replyTo).split('\n\n');
    const showsCustom = hasCustomMessage(draft);
    const storedIsCustom = hasCustomMessage(reminder?.CustomMessage);

    /**
     * Schrijft de tekst weg en sluit het venster; bij een fout blijft het open.
     * @param {string} value - Getrimde eigen tekst, of '' voor de standaardtekst
     */
    const write = async (value) => {
        setSaving(true);
        setSaveError('');
        try {
            await db.collection('TaskReminders').doc(reminder.docid).update({ CustomMessage: value });
        } catch (err) {
            // Zichtbaar laten: de gebruiker moet weten dat er niets is opgeslagen.
            setSaveError(`Opslaan is niet gelukt: ${err?.message || err}`);
            setSaving(false);
            return;
        }
        setSaving(false);
        setSaved('flex');
        setModalOpen(false);
    };

    const saveHandler = () => write(draft.trim());
    const useDefaultHandler = () => write('');

    const draftChangeHandler = (e) => {
        // Eerst uitlezen, dan pas de state zetten.
        // AI-CONTEXT: See docs/pitfalls.md#state-updater-reads-event-target-too-late
        const value = e.target.value;
        setDraft(value);
    };

    return (
        <Modal
            modalOpen={modalOpen}
            setModalOpen={setModalOpen}
            pageHeader="Tekst van de herinnering"
            content={
                <div className="reminder-text-modal">
                    <div className="reminder-text-modal-preview">
                        {showsCustom ? (
                            <>
                                <p>{greeting},</p>
                                <div className="reminder-text-modal-replaceable">
                                    {/* Regelovergangen blijven staan via white-space: pre-line in de CSS. */}
                                    <p>{draft.trim()}</p>
                                </div>
                            </>
                        ) : (
                            <div className="reminder-text-modal-replaceable">
                                <p>{greeting}, {firstParagraph}</p>
                                <p>{secondParagraph}</p>
                            </div>
                        )}
                        <p>Vriendelijke groet,</p>
                        <p>{reminderSignoff(companyName || '')}</p>
                    </div>
                    <label className="reminder-text-modal-label">
                        <strong>Eigen tekst</strong>
                        <textarea
                            value={draft}
                            onChange={draftChangeHandler}
                            placeholder="Leeg laten = standaardtekst"
                        />
                    </label>
                    {saveError && <p className="reminder-text-modal-error">{saveError}</p>}
                </div>
            }
            actions={
                <>
                    <button id="modal-cancel-button" type="button" onClick={() => setModalOpen(false)}>Annuleren</button>
                    {storedIsCustom && (
                        <button
                            type="button"
                            className="reminder-text-modal-default-button"
                            onClick={useDefaultHandler}
                            disabled={saving}
                        >
                            Standaardtekst gebruiken
                        </button>
                    )}
                    <button id="modal-save-button" type="button" onClick={saveHandler} disabled={saving}>Opslaan</button>
                </>
            }
        />
    );
};

export default ReminderTextModal;
